// countryFlag — turn a country (a NAME like "Spain", an ISO-3166 alpha-2 code
// like "ES", or the small {name, code, image_url} object the API returns) into a
// flag emoji for the inbox identity lines (Design Bible D19: "flags instead of
// text country codes where the country is known").
//
// The backend stamps club_country (always) and, for people who have a source
// field, a nationality NAME string (V3) on the channel user. Agents have no
// nationality source yet, so their person flag is simply absent — never faked
// (D19). Returns null when the input can't be resolved to a country, so callers
// render nothing rather than a broken glyph.
//
// Note: regional-indicator flag emojis do not render as flags on every OS
// (Windows shows the letter pair). That is an OS font limitation, not a data
// bug; emoji is the design's chosen representation and degrades to the ISO code.

// A pragmatic name -> ISO alpha-2 map: football nations + common countries, with
// the aliases the platform's data is likely to carry. Lowercased keys.
const NAME_TO_ISO = {
  afghanistan: "AF", albania: "AL", algeria: "DZ", andorra: "AD", angola: "AO",
  argentina: "AR", armenia: "AM", australia: "AU", austria: "AT", azerbaijan: "AZ",
  bahrain: "BH", bangladesh: "BD", belarus: "BY", belgium: "BE", benin: "BJ",
  bolivia: "BO", "bosnia and herzegovina": "BA", bosnia: "BA", botswana: "BW",
  brazil: "BR", bulgaria: "BG", "burkina faso": "BF", burundi: "BI", cameroon: "CM",
  canada: "CA", "cape verde": "CV", chile: "CL", china: "CN", colombia: "CO",
  congo: "CG", "dr congo": "CD", "democratic republic of the congo": "CD",
  "costa rica": "CR", croatia: "HR", cuba: "CU", cyprus: "CY",
  "czech republic": "CZ", czechia: "CZ", denmark: "DK", "dominican republic": "DO",
  ecuador: "EC", egypt: "EG", "el salvador": "SV", england: "GB", estonia: "EE",
  ethiopia: "ET", finland: "FI", france: "FR", gabon: "GA", gambia: "GM",
  georgia: "GE", germany: "DE", ghana: "GH", greece: "GR", guatemala: "GT",
  guinea: "GN", haiti: "HT", honduras: "HN", "hong kong": "HK", hungary: "HU",
  iceland: "IS", india: "IN", indonesia: "ID", iran: "IR", iraq: "IQ",
  ireland: "IE", "republic of ireland": "IE", israel: "IL", italy: "IT",
  "ivory coast": "CI", "cote d'ivoire": "CI", jamaica: "JM", japan: "JP",
  jordan: "JO", kazakhstan: "KZ", kenya: "KE", kosovo: "XK", kuwait: "KW",
  latvia: "LV", lebanon: "LB", libya: "LY", liechtenstein: "LI", lithuania: "LT",
  luxembourg: "LU", macedonia: "MK", "north macedonia": "MK", malaysia: "MY",
  mali: "ML", malta: "MT", mexico: "MX", moldova: "MD", montenegro: "ME",
  morocco: "MA", mozambique: "MZ", netherlands: "NL", holland: "NL",
  "new zealand": "NZ", nigeria: "NG", "northern ireland": "GB", norway: "NO",
  oman: "OM", pakistan: "PK", panama: "PA", paraguay: "PY", peru: "PE",
  philippines: "PH", poland: "PL", portugal: "PT", qatar: "QA", romania: "RO",
  russia: "RU", "saudi arabia": "SA", scotland: "GB", senegal: "SN",
  serbia: "RS", singapore: "SG", slovakia: "SK", slovenia: "SI",
  "south africa": "ZA", "south korea": "KR", korea: "KR", "korea republic": "KR",
  spain: "ES", sweden: "SE", switzerland: "CH", syria: "SY", taiwan: "TW",
  tanzania: "TZ", thailand: "TH", togo: "TG", tunisia: "TN", turkey: "TR",
  "turkiye": "TR", uganda: "UG", ukraine: "UA", "united arab emirates": "AE",
  uae: "AE", "united kingdom": "GB", uk: "GB", "united states": "US",
  usa: "US", "united states of america": "US", uruguay: "UY", uzbekistan: "UZ",
  venezuela: "VE", vietnam: "VN", wales: "GB", yemen: "YE", zambia: "ZM",
  zimbabwe: "ZW",
};

// Non-ISO country codes the platform's data sources emit, mapped onto the ISO
// alpha-2 code they actually mean. The UK home nations are the live case: the
// API stamps country.code "en" (England), "sct"/"sco" (Scotland), "wal"/"wls"
// (Wales) and "nir" (Northern Ireland). None of those is an ISO alpha-2
// country code, so an unmapped code resolves to a flag asset that does not
// exist and the flag renders as an empty box. Keys are lowercased.
// NB: only codes that are NOT assigned ISO alpha-2 codes may appear here
// ("ni" is Nicaragua and is deliberately absent).
const CODE_ALIASES = {
  en: "GB", eng: "GB", sct: "GB", sco: "GB", wal: "GB", wls: "GB",
  nir: "GB", uk: "GB",
};

// The UK home nations have their own flag assets (flagcdn subdivision codes),
// so image-flag surfaces render the real St George's cross / saltire / dragon
// / Ulster banner rather than a generic Union Jack. Keys are lowercased raw
// codes as delivered by the API. Emoji surfaces cannot express these (there is
// no two-letter regional-indicator pair for them) and keep the GB alias above.
const CODE_TO_FLAG_ASSET = {
  en: "gb-eng", eng: "gb-eng", "gb-eng": "gb-eng",
  sct: "gb-sct", sco: "gb-sct", "gb-sct": "gb-sct",
  wal: "gb-wls", wls: "gb-wls", "gb-wls": "gb-wls",
  nir: "gb-nir", "gb-nir": "gb-nir",
};

// The raw code a country shape carries (before aliasing), or null.
const rawCode = (country) => {
  if (!country) return null;
  if (typeof country === "object") {
    const code =
      country.code || country.iso || country.country_code || country.iso2;
    return code ? String(code).trim() : null;
  }
  const raw = String(country).trim();
  // A bare 2-3 letter string is a code, not a country name.
  return /^[A-Za-z]{2,3}$/.test(raw) ? raw : null;
};

// Normalise a raw code onto ISO alpha-2, resolving the non-ISO aliases above.
const normaliseCode = (raw) => {
  const code = String(raw).trim().toUpperCase();
  return CODE_ALIASES[code.toLowerCase()] || code;
};

// ISO alpha-2 -> regional-indicator emoji.
const isoToEmoji = (iso) => {
  const code = String(iso).trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  const base = 0x1f1e6; // regional indicator "A"
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(base + (code.charCodeAt(0) - a)) +
    String.fromCodePoint(base + (code.charCodeAt(1) - a));
};

// Resolve any accepted country shape to an ISO alpha-2 code (or null).
// Exported so image-flag surfaces (V2 agent-request feed) can reuse the same
// name/code/object resolution for their flag-asset URLs.
export const toIso = (country) => {
  if (!country) return null;
  if (typeof country === "object") {
    const code = country.code || country.iso || country.country_code || country.iso2;
    if (code) return normaliseCode(code);
    return toIso(country.name || country.country || country.country_name || null);
  }
  const raw = String(country).trim();
  if (!raw) return null;
  // Already an alpha-2 code (or a non-ISO code alias such as "eng"/"sct").
  if (/^[A-Za-z]{2}$/.test(raw)) return normaliseCode(raw);
  if (/^[A-Za-z]{3}$/.test(raw) && CODE_ALIASES[raw.toLowerCase()]) {
    return CODE_ALIASES[raw.toLowerCase()];
  }
  // A country name (case/space-insensitive lookup).
  const key = raw.toLowerCase().replace(/\s+/g, " ");
  return NAME_TO_ISO[key] || null;
};

// toFlagAssetCode — the flag-asset path code for a country name/code/object,
// or null. Same resolution as toIso, except the UK home nations get their own
// subdivision asset instead of collapsing to the Union Jack. Image-flag
// surfaces use this; emoji surfaces use toIso/countryToFlag.
export const toFlagAssetCode = (country) => {
  const raw = rawCode(country);
  if (raw) {
    const asset = CODE_TO_FLAG_ASSET[raw.toLowerCase()];
    if (asset) return asset;
  }
  const iso = toIso(country);
  return iso ? iso.toLowerCase() : null;
};

// countryToFlag — emoji flag for a country name/code/object, or null.
export const countryToFlag = (country) => {
  const iso = toIso(country);
  return iso ? isoToEmoji(iso) : null;
};

export default countryToFlag;
